import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { API_BASE_URL } from '../config/api';
import { Download, FileText, TrendingUp, Clock } from 'lucide-react';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

export default function AdminDownloads() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'admin') { navigate('/'); return; }
    fetchData();
  }, [user, authLoading]);

  const fetchData = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/papers/analytics/downloads`, {
        headers: authHeaders(), withCredentials: true
      });
      setData(data);
    } catch (e) {
      console.error('Error fetching download analytics:', e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E3A5F]"></div>
        </div>
      </AdminLayout>
    );
  }

  const topPaper = data?.top_papers?.[0];

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="admin-downloads-title">Reading Analytics</h1>
        <p className="text-gray-600">See which research papers resonate most with your members</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Downloads</p>
              <p className="text-3xl font-bold text-[#1E3A5F]" data-testid="stat-total-downloads">{data?.total_downloads ?? 0}</p>
            </div>
            <Download className="h-10 w-10 text-[#E87722] opacity-50" />
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Papers in Library</p>
              <p className="text-3xl font-bold text-[#1E3A5F]">{data?.total_papers ?? 0}</p>
            </div>
            <FileText className="h-10 w-10 text-[#E87722] opacity-50" />
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-sm text-gray-600 mb-1">Most Popular</p>
              <p className="text-sm font-bold text-[#1E3A5F] line-clamp-2">{topPaper?.title || '—'}</p>
            </div>
            <TrendingUp className="h-10 w-10 text-green-600 opacity-50 flex-shrink-0" />
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Top Papers Table */}
        <Card className="p-6 lg:col-span-2">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Papers by Downloads</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="downloads-table">
              <thead>
                <tr className="border-b text-left text-gray-600">
                  <th className="py-3 pr-4 font-semibold">#</th>
                  <th className="py-3 pr-4 font-semibold">Paper</th>
                  <th className="py-3 pr-4 font-semibold">Pillar</th>
                  <th className="py-3 font-semibold text-right">Downloads</th>
                </tr>
              </thead>
              <tbody>
                {(data?.top_papers || []).map((p, idx) => (
                  <tr key={p.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 pr-4 text-gray-400">{idx + 1}</td>
                    <td className="py-3 pr-4 font-medium text-[#1E3A5F]">{p.title}</td>
                    <td className="py-3 pr-4"><Badge variant="outline" className="text-xs">{p.pillar}</Badge></td>
                    <td className="py-3 text-right font-bold text-[#E87722]">{p.downloads ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Recent Activity */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-[#E87722]" />Recent Downloads
          </h2>
          {(data?.recent_downloads || []).length === 0 ? (
            <p className="text-sm text-gray-500 italic" data-testid="no-recent-downloads">
              No download activity yet. Events are logged from now on whenever a member downloads a paper.
            </p>
          ) : (
            <div className="space-y-3" data-testid="recent-downloads-list">
              {data.recent_downloads.map((d, idx) => (
                <div key={idx} className="border-b pb-3 last:border-b-0">
                  <p className="text-sm font-medium text-[#1E3A5F] line-clamp-1">{d.paper_title}</p>
                  <p className="text-xs text-gray-500">
                    {d.user_name || d.user_email} • {new Date(d.at).toLocaleString('en-IN')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
}
